import { useAtomValue, useSetAtom } from "jotai";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import { useState } from "react";

import useCreateModuleFromCSV from "../../../api/api-hooks/tools/useCreateModuleFromCSV";
import { setSchemaAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import AppDrawer, { DrawerError, DrawerSuccess } from "../AppDrawer";
import { Field, Module } from "../../../types/TenantInterfaces";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { getCSVHeaders } from "../../Inputs/ExcelCSVInput";
import AppSpinner from "../../../elements/AppSpinner";
import FileSelectionPage from "./FileSelectionPage";
import TenantAPI from "../../../api/api/TenantAPI";
import ImportCSVDrawer from "../ImportCSVDrawer";
import ConfigureModule from "./ConfigureModule";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Pages =
  | "File Selection"
  | "Processing File..."
  | "Data Source Result"
  | "Next Steps";

export default function CreateModuleFromCSVDrawer({ open, onClose }: Props) {
  const tenantID = useAtomValue(tenantIDAtom);
  const setSchema = useSetAtom(setSchemaAtom);

  const [page, setPage] = useState<Pages>("File Selection"); // The current page in the drawer.
  const [submitting, setSubmitting] = useState(false); // Whether or not CSV file is being processed.
  const [fileSelectionOpen, setFileSelectionOpen] = useState(true);

  const [file, setFile] = useState<any>(null);
  const [fileRecordID, setFileRecordID] = useState("");

  const [success, setSuccess] = useState<DrawerSuccess | null>(null);
  const [error, setError] = useState<DrawerError | null>(null);

  const [modSchema, setModSchema] = useState<Module>(getDefaultMod()); // The new module schema.
  const [uploadedFields, setUploadedFields] = useState<Field[]>(); // The proposed field schemas provided from the API. Saved in state in case user wants to reset their changes to them.
  const [existingHeaders, setExistingHeaders] = useState<string[] | null>(null); // Existing headers to be passed along to ImportCSVDrawer. Prevents the need of uploading the same file twice.

  const { createModuleFromCSV } = useCreateModuleFromCSV();

  return (
    <>
      <AppDrawer
        open={open}
        onClose={handleClose}
        backdrop={"static"}
        size="md"
        headerTitle={page}
        success={success}
        error={error}
        bodyCSS="pt-2"
        headerOptions={
          <>
            {page === "File Selection" && file && (
              <Button color="primary" size="sm" onClick={startProcessing}>
                Next
              </Button>
            )}
            {page === "Data Source Result" && (
              <Button
                color="primary"
                size="sm"
                onClick={onCreateModule}
                disabled={submitting}
                style={{ width: 160 }}
              >
                {submitting ? (
                  <AppSpinner size="sm" color="white" />
                ) : (
                  "Create Data Source"
                )}
              </Button>
            )}
          </>
        }
        body={
          <>
            {page === "File Selection" && (
              <FileSelectionPage
                file={file}
                setFile={setFile}
                fileSelectionOpen={fileSelectionOpen}
                setFileSelectionOpen={setFileSelectionOpen}
                fileRecordID={fileRecordID}
                setFileRecordID={setFileRecordID}
                startProcessing={startProcessing}
              />
            )}
            {page === "Processing File..." && (
              <div className="shadow-sm border p-4 rounded bg-ultralight">
                <h3>
                  <AppSpinner
                    size="sm"
                    className="mr-2"
                    style={{ position: "relative", top: -4 }}
                  />
                  Your file is being processed...
                </h3>
                <p className="m-0">
                  Hang tight, we&apos;re processing your file. This may take a
                  few minutes...
                </p>
              </div>
            )}
            {page === "Data Source Result" && uploadedFields && (
              <ConfigureModule
                modSchema={modSchema}
                setModSchema={setModSchema}
                uploadedFields={uploadedFields}
              />
            )}
            {page === "Next Steps" && (
              <div className="border border-superlight bg-ultralight rounded p-4 mt-4">
                <div className="d-flex align-items-center">
                  <div style={{ fontSize: 80 }}>&#129395;</div>
                  <div className="ml-5">
                    <h2>Success! We created your data source!</h2>
                    <div className="mb-4">
                      Your data source was successfully created. You can now
                      import the data from the Excel/CSV file, or go to your
                      data source settings.
                    </div>
                    <Button color="primary" onClick={startImportProcess}>
                      Import File Data
                    </Button>{" "}
                    <Link
                      to={`/clouds/${tenantID}/settings/modules/manage/${modSchema.APIName}/fields`}
                    >
                      <Button>Go to Settings</Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </>
        }
      />
      <ImportCSVDrawer
        open={!!existingHeaders}
        onClose={() => handleClose()}
        onCreate={() => handleClose()}
        // @ts-ignore
        moduleAPIName={modSchema.APIName}
        fieldSchema={null}
        startingFile={file}
        startingHeaders={existingHeaders}
        key={JSON.stringify(existingHeaders)}
      />
    </>
  );

  function handleClose() {
    if (page === "File Selection" && fileRecordID) {
      TenantAPI.deleteFiles({
        type: "fileID",
        fileID: fileRecordID,
        moduleAPIName: "",
        fieldAPIName: "",
        recordID: "",
      });
    }

    setPage("File Selection");
    setFile(null);
    setFileSelectionOpen(true);
    onClose();
    setModSchema(getDefaultMod());
    setUploadedFields(undefined);
    setExistingHeaders(null);
  }

  // Start processing CSV file.
  function startProcessing() {
    setPage("Processing File...");
    createModuleFromCSV({
      fileRecordID,
      onSuccess: (fields) => {
        setUploadedFields(structuredClone(fields));
        setModSchema((prev) => ({ ...prev, Fields: fields || [] }));
        setSuccess({
          message: "Successfully processed file!",
          onClear: () => setSuccess(null),
        });
        setTimeout(() => setSuccess(null), 5000);
        setPage("Data Source Result");
      },
      onError: (err: string) => {
        setPage("File Selection");
        setError({ message: err, onClear: () => setError(null) });
      },
    });
  }

  async function onCreateModule() {
    if (!modSchema.Name || !modSchema.APIName || !modSchema.Fields.length) {
      return setError({
        message:
          "Missing required fields! Ensure you have a name, API name, and at least one field!",
        onClear: () => setError(null),
      });
    }

    setError(null);
    setSubmitting(true);

    try {
      const newSchema: any = await TenantAPI.addCRMModule(
        modSchema.Name,
        modSchema.APIName,
        modSchema.Fields,
        null,
        modSchema.Templates
      );

      if (!newSchema) throw Error("Something went wrong.");

      setSchema(newSchema);
      setPage("Next Steps");
    } catch (err) {
      setError({
        message: getErrorMessage(err),
        onClear: () => setError(null),
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function startImportProcess() {
    const { newHeaders } = await getCSVHeaders(file);

    setExistingHeaders(newHeaders);
  }
}

function getDefaultMod() {
  return {
    Name: "",
    APIName: "",
    Fields: [],
    Default: false,
    Protected: false,
    Templates: {
      QuickSearch: [],
      RecordListView: {
        Fields: ["Created", "LastUpdated"],
      },
      RecordDetailView: {
        PageTitle: "",
        Layouts: [],
      },
      RecordDetailViewSubModules: {
        Submodules: [],
        Tabs: [],
      },
    },
  };
}
